import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';
import type {
    Action,
    ActionRevision,
    ActionWithRevisions,
    CreateActionParams,
    UpdateActionParams,
    DeleteActionParams,
    FetchActionsParams,
    CreateRevisionParams,
    UpdateRevisionParams,
    DeleteRevisionParams,
    FetchActionRevisionsParams,
    Execution,
    CreateExecutionParams,
    UpdateExecutionParams,
    DeleteExecutionParams,
    FetchExecutionsParams,
    FetchExecutionsByObjectParams,
} from './types/actions';
import { JWTHttpClient } from '@/services/JWTHttpClient';

// Get the actions service URL from environment variable
const ACTIONS_SERVICE_URL =
    import.meta.env.VITE_ACTIONS_SERVICE_URL || 'https://actions-svc-230937369904.us-central1.run.app';

// Create JWT HTTP client for Actions Service API calls
const jwtHttpClient = new JWTHttpClient(ACTIONS_SERVICE_URL);

/**
 * Composable that provides access to Actions API functions.
 * This safely initializes the httpClient with authStore only when called
 * inside components or other composables after Pinia is ready.
 */
export function useActionsAPI() {
    const authStore = useAuthStore();
    const httpClient = getHttpClient(authStore);

    return {
        async fetchActions(params: FetchActionsParams = {}): Promise<Action[]> {
            try {
                console.log('⚡ ActionsAPI: Fetching actions with params:', params);

                const queryParams: Record<string, string> = {};

                if (params.orgId) {
                    queryParams.orgId = params.orgId;
                }

                if (params.namespace) {
                    queryParams.namespace = params.namespace;
                }

                const response = await jwtHttpClient.get<Action[]>('v1/actions', queryParams, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Actions fetched successfully:', response);
                return response;
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching actions:', error);
                throw new Error('Failed to fetch actions');
            }
        },

        async getActionById(id: string): Promise<Action> {
            try {
                console.log('⚡ ActionsAPI: Fetching action by ID:', id);

                const response = await jwtHttpClient.get<Action>(`v1/actions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Action fetched successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Action not found');
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching action by ID:', error);
                throw error;
            }
        },

        async createAction(params: CreateActionParams): Promise<Action> {
            try {
                console.log('⚡ ActionsAPI: Creating action:', params);

                const response = await jwtHttpClient.post<Action>('v1/actions', params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Action created successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to create action');
            } catch (error) {
                console.error('❌ ActionsAPI: Error creating action:', error);
                throw error;
            }
        },

        async updateAction(params: UpdateActionParams): Promise<Action> {
            try {
                console.log('⚡ ActionsAPI: Updating action:', params);

                const response = await jwtHttpClient.put<Action>(`v1/actions/${params.id}`, params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Action updated successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to update action');
            } catch (error) {
                console.error('❌ ActionsAPI: Error updating action:', error);
                throw error;
            }
        },

        async deleteAction(params: DeleteActionParams): Promise<boolean> {
            try {
                console.log('⚡ ActionsAPI: Deleting action:', params.id);

                await jwtHttpClient.delete<any>(`v1/actions/${params.id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Action deleted successfully');
                return true;
            } catch (error) {
                console.error('❌ ActionsAPI: Error deleting action:', error);
                throw error;
            }
        },

        // Revision methods
        async fetchActionRevisions(params: FetchActionRevisionsParams): Promise<ActionRevision[]> {
            try {
                console.log('⚡ ActionsAPI: Fetching revisions for action:', params.actionId);

                const response = await jwtHttpClient.get<ActionRevision[]>(
                    `v1/actions/${params.actionId}/revisions`,
                    undefined,
                    {
                        'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                    }
                );

                console.log('⚡ ActionsAPI: Revisions fetched successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to fetch action revisions');
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching action revisions:', error);
                throw error;
            }
        },

        async getRevisionById(id: string): Promise<ActionRevision> {
            try {
                console.log('⚡ ActionsAPI: Fetching revision by ID:', id);

                const response = await jwtHttpClient.get<ActionRevision>(`v1/actions/-/revisions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Revision fetched successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Revision not found');
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching revision by ID:', error);
                throw error;
            }
        },

        async createRevision(params: CreateRevisionParams): Promise<ActionRevision> {
            try {
                console.log('⚡ ActionsAPI: Creating revision:', params);

                const response = await jwtHttpClient.post<ActionRevision>('v1/actions/-/revisions', params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Revision created successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to create revision');
            } catch (error) {
                console.error('❌ ActionsAPI: Error creating revision:', error);
                throw error;
            }
        },

        async updateRevision(params: UpdateRevisionParams): Promise<ActionRevision> {
            try {
                console.log('⚡ ActionsAPI: Updating revision:', params);

                const response = await jwtHttpClient.put<ActionRevision>(
                    `v1/actions/-/revisions/${params.id}`,
                    params,
                    undefined,
                    {
                        'content-type': 'application/json',
                        'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                    }
                );

                console.log('⚡ ActionsAPI: Revision updated successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to update revision');
            } catch (error) {
                console.error('❌ ActionsAPI: Error updating revision:', error);
                throw error;
            }
        },

        async deleteRevision(params: DeleteRevisionParams): Promise<boolean> {
            try {
                console.log('⚡ ActionsAPI: Deleting revision:', params.id);

                await jwtHttpClient.delete<any>(`v1/actions/-/revisions/${params.id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Revision deleted successfully');
                return true;
            } catch (error) {
                console.error('❌ ActionsAPI: Error deleting revision:', error);
                throw error;
            }
        },

        async getActionWithRevisions(actionId: string): Promise<ActionWithRevisions> {
            try {
                console.log('⚡ ActionsAPI: Fetching action with revisions:', actionId);

                // Fetch the action
                const action = await this.getActionById(actionId);

                // Fetch the revisions
                const revisions = await this.fetchActionRevisions({ actionId });

                // Get the most recent published revision
                const currentRevision = revisions.length > 0 ? revisions[0] : undefined;

                const actionWithRevisions: ActionWithRevisions = {
                    ...action,
                    revisions,
                    currentRevision,
                };

                console.log('✅ ActionsAPI: Action with revisions fetched successfully:', actionWithRevisions);
                return actionWithRevisions;
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching action with revisions:', error);
                throw error;
            }
        },

        // Execution methods
        async fetchExecutions(params: FetchExecutionsParams = {}): Promise<Execution[]> {
            try {
                console.log('⚡ ActionsAPI: Fetching executions with params:', params);

                const queryParams: Record<string, string> = {};

                if (params.actionId) {
                    queryParams.actionId = params.actionId;
                }

                const response = await jwtHttpClient.get<Execution[]>('v1/executions', queryParams, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Executions fetched successfully:', response);
                return response;
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching executions:', error);
                throw new Error('Failed to fetch executions');
            }
        },

        async fetchExecutionsByObject(params: FetchExecutionsByObjectParams): Promise<Execution[]> {
            try {
                console.log('⚡ ActionsAPI: Fetching executions by object:', params);

                const queryParams: Record<string, string> = {
                    object: params.object,
                    objectId: params.objectId,
                };

                const response = await jwtHttpClient.get<Execution[]>('v1/executions/by-object', queryParams, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Executions by object fetched successfully:', response);
                return response;
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching executions by object:', error);
                throw new Error('Failed to fetch executions by object');
            }
        },

        async getExecutionById(id: string): Promise<Execution> {
            try {
                console.log('⚡ ActionsAPI: Fetching execution by ID:', id);

                const response = await jwtHttpClient.get<Execution>(`v1/executions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Execution fetched successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Execution not found');
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching execution by ID:', error);
                throw error;
            }
        },

        async createExecution(params: CreateExecutionParams): Promise<Execution> {
            try {
                console.log('⚡ ActionsAPI: Creating execution:', params);

                const response = await jwtHttpClient.post<Execution>('v1/executions', params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Execution created successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to create execution');
            } catch (error) {
                console.error('❌ ActionsAPI: Error creating execution:', error);
                throw error;
            }
        },

        async updateExecution(params: UpdateExecutionParams): Promise<Execution> {
            try {
                console.log('⚡ ActionsAPI: Updating execution:', params);

                const response = await jwtHttpClient.put<Execution>(`v1/executions/${params.id}`, params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Execution updated successfully:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to update execution');
            } catch (error) {
                console.error('❌ ActionsAPI: Error updating execution:', error);
                throw error;
            }
        },

        async deleteExecution(params: DeleteExecutionParams): Promise<boolean> {
            try {
                console.log('⚡ ActionsAPI: Deleting execution:', params.id);

                await jwtHttpClient.delete<any>(`v1/executions/${params.id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });

                console.log('⚡ ActionsAPI: Execution deleted successfully');
                return true;
            } catch (error) {
                console.error('❌ ActionsAPI: Error deleting execution:', error);
                throw error;
            }
        },
    };
}
